<template>
  <v-card id="tax-summary-dividends-key-points" class="px-4 pt-2 pb-4 my-0 elevation-0">
    <v-card-title class="px-1 pb-2">
      <h1 class="card-title">
        {{ $t(`views.tax.taxSummary.dividendsKeyPoints.title`) }}
      </h1>
      <v-spacer />
      <ExportButton :click="downloadCsv" :loading="exportLoading" />
    </v-card-title>
    <v-card-text class="mt-0 pa-0">
      <BarLoading v-if="loading" />
      <template v-else>
        <v-simple-table dense fixed-header class="mt-4">
          <template v-slot:default>
            <tbody>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.ingresosIntegros.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.ingresosIntegros.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.ingresosIntegros"
                    :currency="user.baseCurrency"
                    :hideDataNoPlanUser="hideDataNoPlanUser"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.retenciones.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.retenciones.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.retenciones"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.commissions.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.commissions.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.commissions"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.rendimientosNetos.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.rendimientosNetos.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.rendimientosNetos"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{
                      $t(
                        `views.tax.taxSummary.dividendsKeyPoints.impuestoSatisfechoExtranjero.title`
                      )
                    }}
                    <InfoIcon
                      :size="15"
                      :text="
                        $t(
                          `views.tax.taxSummary.dividendsKeyPoints.impuestoSatisfechoExtranjero.info`
                        )
                      "
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.impuestoSatisfechoExtranjero"
                    :currency="user.baseCurrency"
                    :hideDataNoPlanUser="hideDataNoPlanUser"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.couponsTotal.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.couponsTotal.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.coupons"
                    :value="yearSummary.coupons.totalBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.dividendsKeyPoints.couponsRetention.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.dividendsKeyPoints.couponsRetention.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.coupons"
                    :value="yearSummary.coupons.destinationRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { getTaxSummaryCsv } from '@/lib/api.js'
import Currency from '@/views/components/common/Currency'
import InfoIcon from '@/views/components/common/InfoIcon'
import BarLoading from '@/views/components/core/BarLoading.vue'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { Currency, InfoIcon, BarLoading, ExportButton },
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    yearSelected: {
      type: String,
      required: true,
    },
    yearSummary: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      exportLoading: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  methods: {
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await getTaxSummaryCsv({
          startDate: this.startDate,
          endDate: this.endDate,
          section: 'dividendsKeyPoints',
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        const title = `${this.$t('document.title.tax')} ${this.yearSelected}. ${this.$t(
          `views.tax.taxSummary.dividendsKeyPoints.title`
        )}`
        FileSaver.saveAs(blob, `Filios - ${title} - ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#tax-summary-dividends-key-points {
  height: 100%;
}
</style>
